import { useState, ChangeEvent } from 'react'
import classNames from 'classnames'
import {
  Box,
  Button,
  Grid,
  Link,
  Paper,
  TextField,
  Typography,
  useMediaQuery
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

import {
  flexItemCenter,
  flexItemColumn,
  loginSignupButton,
  loginSignupImageStyle
} from '../../global'
import logo from '../../images/logo-wfgps.svg'
import { forgotPassword } from '../../services/AuthService'
import { appTheme } from '../../theme'

var isMobView = false

const useStyles = makeStyles({
  root: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: ' rgba(0, 0, 0, 0.2)'
      },
      '&:hover fieldset': {
        borderColor: '#006BD7'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#006BD7'
      }
    },

    '& .MuiFormControl-root': {
      margin: '10px',
      [appTheme.breakpoints.up('md')]: {
        margin: '10px'
      }
    }
  },
  buttonDesing: {
    background: '#253D82',
    boxShadow: 'none',
    width: '270px !important',
    margin: '14px auto 0!important',
    display: 'flex',
    '&:hover': {
      background: '#253D82'
    }
  },
  heightVh_16px: {
    height: 'auto',
    [appTheme.breakpoints.up('md')]: {
      height: 'calc(100% - 20px)'
    }
  },
  inputBoxStyle: {
    width: '100%',
    display: 'block',
    [appTheme.breakpoints.up('md')]: {
      width: '560px',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between'
    }
  },
  inputStyle: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    [appTheme.breakpoints.up('md')]: {
      width: '270px !important'
    }
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderRadius: '8px'
      }
    }
  },
  widthVh_150px: {
    width: '100%',
    [appTheme.breakpoints.up('md')]: {
      width: 'calc(100% - 150px)'
    }
  },
  boxWrapper: {
    padding: '15px',
    [appTheme.breakpoints.up('md')]: {
      padding: '0'
    }
  },
  closeIcon: {
    width: '100px',
    float: 'right',
    margin: '0.5rem',
    position: 'static',
    [appTheme.breakpoints.down('lg')]: {
      right: '1rem',
      margin: '0',
      position: 'absolute',
      top: '1rem'
    }
  }
})
interface forgotPasswordProps {
  classList: any
  modelClose: any
  handleLoginOpen: any
  handleResetPasswordOpen: (email: string) => void
  resetPasswordEmail?: string
}

const emailValidator = (email: string) => {
  if (email === '') {
    return 'Email is required'
  } else if (!new RegExp(/\S+@\S+\.\S+/).test(email)) {
    return 'Incorrect email format'
  }
  return ''
}

function ForgotPassword(props: forgotPasswordProps) {
  const classes = useStyles()
  isMobView = useMediaQuery(appTheme.breakpoints.down('md'))

  const [forgotPasswordState, setForgotPasswordState] = useState({
    email: props.resetPasswordEmail || '',
    newPassword: '',
    confirmPassword: ''
  })
  const [emailError, setEmailError] = useState('')

  const changeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    let name = event.target.name
    let value = event.target.value
    setForgotPasswordState({ ...forgotPasswordState, [name]: value })
    name === 'email' && setEmailError(emailValidator(value))
  }

  const validateForgotPassword = () => {
    let validate = true
    if (emailValidator(forgotPasswordState.email) !== '') {
      validate = false
      setEmailError(emailValidator(forgotPasswordState.email))
    }
    return validate
  }

  const forgotPasswordUser = async () => {
    if (!validateForgotPassword()) {
      return
    } else {
      try {
        let { data } = await forgotPassword(forgotPasswordState.email)
        if (data?.forgotPassword?.statusCode === 200) {
          props.modelClose()
          props.handleResetPasswordOpen(forgotPasswordState.email.trim())
        } else {
          setEmailError(data?.forgotPassword?.message)
        }
      } catch (err) {
        console.log('Error', err)
      }
    }
  }

  const handleLoginOpen = () => {
    props.handleLoginOpen()
    props.modelClose()
  }

  return (
    <>
      <Box className={classes.boxWrapper} sx={{ flexGrow: 1 }}>
        <Grid container spacing={2}>
          {!isMobView && (
            <Grid color='info' item sm={12} md={3}>
              <Paper
                style={flexItemCenter}
                className={classes.heightVh_16px}
                sx={{
                  bgcolor: 'primary.main',
                  p: 1.6,
                  m: 1.2,
                  mt: { sm: 4, md: 1.2 }
                }}
                color='info'
              >
                <img
                  style={loginSignupImageStyle}
                  src={logo}
                  alt='Logo'
                  width='100%'
                  max-width='224px'
                />
              </Paper>
            </Grid>
          )}
          <Grid item xs={12} md={9} width='100%'>
            <Box>
              <CloseRoundedIcon
                onClick={props.modelClose}
                className={classes.closeIcon}
                sx={{ fontSize: 20, color: '#253D82', fontWeight: 200 }}
              />
            </Box>
            <Box sx={{ p: 2, pb: 4 }}>
              <Paper style={flexItemColumn} className={classes.heightVh_16px}>
                <Typography
                  sx={{ bgcolor: '#FFF5EB', borderRadius: 3, color: 'black', fontSize: 12, fontWeight: 500, mt: { xs: 3, md: 0.5 }, mb: 4, px: 2, py: 2.5 }}
                  variant='body1'
                  align='left'
                  color='primary'
                >
                  <b>Note:</b> Do not close this window until each step has been completed.
                </Typography>
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 600 }}
                  align='left'
                  color='primary'
                >
                  Forgot Password?
                </Typography>
                <Typography
                  sx={{ mb: 4 }}
                  variant='body1'
                  align='left'
                  color='primary'
                >
                  Please provide your email below
                </Typography>
                <Box
                  sx={{
                    ml: { sm: 2.5, xs: 0, md: 0 }
                  }}
                >
                  <Typography
                    sx={{ mb: 1, fontWeight: 400, fontSize: 12 }}
                    align='left'
                    color='inherit'
                  >
                    Log in and password reset instuctions will be sent to your
                    email
                  </Typography>
                </Box>

                <Box className={classes.inputBoxStyle} style={flexItemCenter}>
                  <TextField
                    sx={{ width: '100%' }}
                    size='small'
                    className={classNames(classes.root, classes.inputStyle)}
                    label='Email'
                    type='email'
                    name='email'
                    value={forgotPasswordState.email}
                    onChange={changeHandler}
                    InputLabelProps={{ shrink: true }}
                    helperText={emailError}
                    error={!!emailError}
                  />
                </Box>

                <Button
                  className={classes.buttonDesing}
                  sx={loginSignupButton}
                  style={{
                    textTransform: 'none',
                    fontSize: 14,
                    fontWeight: 500
                  }}
                  variant='contained'
                  onClick={forgotPasswordUser}
                >
                  Send Email
                </Button>
                <Link
                  color='secondary'
                  underline='none'
                  sx={{
                    display: 'inline',
                    cursor: 'pointer',
                    ml: 1,
                    mt: 2,
                    fontSize: 12,
                    fontWeight: 600
                  }}
                  onClick={forgotPasswordUser}
                >
                  Resend email
                </Link>
              </Paper>
            </Box>
            <Grid
              sx={{
                pb: 4,
                display: 'flex',
                flexDirection: {
                  sm: 'column-reverse',
                  md: 'row',
                  xs: 'column-reverse'
                }
              }}
            >
              <Grid
                item
                sm={12}
                md={12}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 400 }}>
                  Having issues?
                </Typography>
                <Link
                  href='mailto:events@workforcegps.org'
                  color='secondary'
                  underline='none'
                  sx={{
                    display: 'inline',
                    ml: 1,
                    fontSize: 12,
                    fontWeight: 600
                  }}
                >
                  Contact Support
                </Link>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

export default ForgotPassword
